import React, { useEffect, useState } from 'react';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { MessageCircle, FileText, Send, Clock, CheckCircle2, X } from 'lucide-react';

export default function SupportTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/tickets');
      setTickets(data.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      return toast.error('Subject and message are required.');
    }
    setSubmitting(true);
    try {
      await axios.post('/api/tickets', form);
      toast.success('Ticket submitted successfully!');
      setForm({ subject: '', message: '' });
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket({ ...ticket, loading: true });
    try {
      const { data } = await axios.get(`/api/tickets/${ticket._id}`);
      setSelectedTicket(data.ticket);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ticket');
      setSelectedTicket(null);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return toast.error('Reply message is required.');
    if (!selectedTicket) return;
    setReplying(true);
    try {
      const { data } = await axios.post(`/api/tickets/${selectedTicket._id}/reply`, { message: replyMessage });
      toast.success('Reply sent to support.');
      setSelectedTicket(data.ticket);
      setReplyMessage('');
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reply failed');
    } finally {
      setReplying(false);
    }
  };

  const renderStatus = (status) => {
    const mapping = {
      open: { label: 'Open', className: 'bg-primary-500/20 text-primary-200' },
      pending: { label: 'Pending', className: 'bg-amber-500/20 text-amber-200' },
      closed: { label: 'Closed', className: 'bg-gray-600/20 text-gray-200' },
    };
    return <span className={`text-xs px-2 py-1 rounded-full ${mapping[status]?.className || 'bg-gray-600/20 text-gray-200'}`}>{mapping[status]?.label || status}</span>;
  };

  return (
    <UserLayout>
      <div className="animate-fade-in grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-primary-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Support Tickets</h1>
                <p className="text-gray-400 text-sm">Create a ticket and track replies from support.</p>
              </div>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-2xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  placeholder="Enter a brief subject"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Message</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-2xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  placeholder="Describe your issue in detail"
                />
              </div>
              <button type="submit" disabled={submitting}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white rounded-2xl px-5 py-3 font-semibold transition-colors">
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />} Submit Ticket
              </button>
            </form>
          </div>

          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageCircle className="w-5 h-5 text-amber-400" />
              <div>
                <h2 className="text-xl font-semibold text-white">Your Tickets</h2>
                <p className="text-gray-400 text-sm">Review ticket status and latest activity.</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-300">
                <thead>
                  <tr className="text-gray-400 uppercase text-xs tracking-[0.12em]">
                    <th className="pb-3">Subject</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Updated</th>
                    <th className="pb-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="py-10 text-center text-gray-500">Loading tickets...</td></tr>
                  ) : tickets.length === 0 ? (
                    <tr><td colSpan={4} className="py-10 text-center text-gray-500">No tickets submitted yet.</td></tr>
                  ) : tickets.map((ticket) => (
                    <tr key={ticket._id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                      <td className="py-3 pr-4 text-white">{ticket.subject}</td>
                      <td className="py-3 pr-4">{renderStatus(ticket.status)}</td>
                      <td className="py-3 pr-4 text-gray-400">{new Date(ticket.updatedAt).toLocaleString()}</td>
                      <td className="py-3">
                        <button onClick={() => openTicket(ticket)}
                          className="text-sm text-primary-300 hover:text-white">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-5 h-5 text-primary-400" />
              <div>
                <h2 className="text-xl font-semibold text-white">Ticket Details</h2>
                <p className="text-gray-400 text-sm">Select a ticket to see the conversation.</p>
              </div>
            </div>

            {!selectedTicket ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-gray-400">
                Select a ticket from the list to view its details.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl bg-dark-800 border border-white/10 p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{selectedTicket.subject}</h3>
                      <p className="text-sm text-gray-400">Status: {renderStatus(selectedTicket.status)}</p>
                    </div>
                    <span className="text-xs text-gray-500">Updated {new Date(selectedTicket.updatedAt).toLocaleString()}</span>
                  </div>
                  <div className="space-y-3">
                    {selectedTicket.messages.map((msg) => (
                      <div key={msg._id} className={`rounded-2xl p-4 ${msg.role === 'admin' ? 'bg-red-500/10 border border-red-500/10' : 'bg-primary-500/10 border border-primary-500/10'}`}>
                        <div className="flex items-center justify-between gap-3 mb-2 text-sm text-gray-300">
                          <span className="font-medium text-white">{msg.role === 'admin' ? 'Support' : 'You'}</span>
                          <span>{new Date(msg.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-200 whitespace-pre-line">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedTicket.status !== 'closed' ? (
                  <form onSubmit={handleReply} className="space-y-4">
                    <textarea
                      rows={4}
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="w-full bg-dark-600 border border-white/10 text-white rounded-2xl px-4 py-3 focus:outline-none focus:border-primary-500"
                      placeholder="Add a follow-up message for support"
                    />
                    <button type="submit" disabled={replying}
                      className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white rounded-2xl px-5 py-3 font-semibold transition-colors">
                      {replying ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />} Send Reply
                    </button>
                  </form>
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-dark-800 p-4 text-gray-400">This ticket is closed and no longer accepts replies.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
